import { Component, Input, OnInit, OnChanges, SimpleChanges, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToggleBtnsComponent } from '../../../../../shared/components/toggle-btns/toggle-btns.component';
import { ScrollTableComponent, TableHeader } from '../../../../../shared/components/tables/scrolled-table/scroll-table.component';
import { LineChartComponent } from '../../../../../shared/components/charts/line-chart/line-chart.component';
import { SelectionComponent, SelectionOption } from '../../../../../shared/components/ui/selection/selection.component';
import { DateRangePickerOutput } from '../../../../../shared/components/date-picker/date-picker.component';
import { ApiService } from '../../../../../core/services/api.service';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { IndividualToasterComponent } from "../../../../../shared/components/loader/loader.component";

dayjs.extend(quarterOfYear);

interface ToggleOption {
  label: string;
  icon?: string;
  tooltip?: string;
  isTable?: boolean;
}

@Component({
  selector: 'app-contract-status',
  imports: [
    CommonModule,
    FormsModule,
    ToggleBtnsComponent,
    LineChartComponent,
    ScrollTableComponent,
    SelectionComponent,
    IndividualToasterComponent
],
  templateUrl: './contract-status.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: ``
})
export class ContractStatusComponent implements OnInit, OnChanges {
  @Input() dataType: 'stage' | 'phase' = 'stage'; // Parent can specify which data to show
  @Input() dateRange!: DateRangePickerOutput;
  @Input() productServerName: string = 'denefits'; // Dynamic product server name
  @Input() requiresAccessToken: boolean = false; // Whether the product requires an access token
  @Input() cocu_id: number = 1;
  @Input() country_id: number = 1;
  isLoading = signal(false)
  // Toggle options for Graph/Table view
  toggleOptions: ToggleOption[] = [
    {
      label: 'Graph',
      icon: 'assets/img/PNG/Vector.png',
      tooltip: 'Click to View Graph',
      isTable: false,
    },
    {
      label: 'Table',
      icon: 'assets/img/PNG/show_table.png',
      tooltip: 'Click to View Table',
      isTable: true,
    },
  ];

  private viewSignal: WritableSignal<'chart' | 'table'> = signal('table');

  // Current selected stage/phase
  selectedStageId: number = 1; // Default to "Registered"
  selectedPhaseId: string = '1'; // Default to "Phase 0"

  // Data for display
  currentData: any = null;
  previousData: any = null;
  chartData: any[] = [];
  previousChartData: any[] = [];
  tableData: any[] = [];
  previousTableData: any[] = [];
  selectorOptions: any[] = [];
  
  // Stats
  currentTotal: number = 0;
  previousTotal: number = 0;
  percentChange: number = 0;

  // Table headers - will be dynamically generated
  tableHeaders: TableHeader[] = [];
  
  // Tooltip properties
  tooltipVisible = false;
  hoveredRowData: any = null;
  tooltipPosition = { x: 0, y: 0 };

  constructor(private apiService: ApiService) { }

  ngOnInit(): void {
    this.updateTableHeaders();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['dateRange'] && changes['dateRange'].currentValue) || 
        (changes['productServerName'] && !changes['productServerName'].firstChange) ||
        changes['cocu_id'] || changes['country_id']) {
      // Update table headers when dateRange changes
      this.updateTableHeaders();
      
      // When dateRange changes, reload data
      if (this.dateRange?.previous_start_date && this.dateRange?.previous_end_date) {
        // Fetch both current and previous period data
        this.fetchData(false, true, false);
        this.fetchData(false, true, true);
      } else {
        // Fetch only current period data
        this.fetchData();
      }
    }
  }

  get view() {
    return this.viewSignal();
  }

  get selectedValue(): number | string {
    return this.dataType === 'stage' ? this.selectedStageId : this.selectedPhaseId;
  }

  updateTableHeaders(): void {
    const unit = this.dateRange?.unit || 'day';
    const dateLabel = this.getDateLabel(unit);
    
    // Format date ranges for current and previous periods using unit-aware formatting
    const currentPeriodLabel = this.dateRange?.start_date && this.dateRange?.end_date
      ? this.formatDateRangeByUnit(this.dateRange.start_date, this.dateRange.end_date, unit)
      : '';
    
    const previousPeriodLabel = this.dateRange?.previous_start_date && this.dateRange?.previous_end_date
      ? this.formatDateRangeByUnit(this.dateRange.previous_start_date, this.dateRange.previous_end_date, unit)
      : '';

    this.tableHeaders = [
      { label: dateLabel, key: 'start', align: 'text-left', noTransform: true },
      { label: `Current${currentPeriodLabel ? ` (${currentPeriodLabel})` : ''}`, key: 'total', align: 'text-left', showProgress: true, showTooltip: true },
      { label: `Previous${previousPeriodLabel ? ` (${previousPeriodLabel})` : ''}`, key: 'total', align: 'text-left', isPrevious: true, showTooltip: true },
    ];
  }

  private getDateLabel(unit: string): string {
    const labels: { [key: string]: string } = {
      'hour': 'Hour',
      'day': 'Date',
      'week': 'Week',
      'month': 'Month',
      'quarter': 'Quarter',
      'year': 'Year'
    };
    return labels[unit] || 'Date';
  }

  private formatStartDate(startDate: string, endDate: string, unit: string): string {
    if (unit === 'week') {
      return `${dayjs(startDate).format('DD MMM')} - ${dayjs(endDate).format('DD MMM')}`;
    } else if (unit === 'day') {
      return `${dayjs(startDate).format('DD MMM')}`;
    } else if (unit === 'quarter') {
      return `${dayjs(startDate).format('MMM YY')} - ${dayjs(endDate).format('MMM YY')}`;
    } else if (unit === 'year') {
      return `${dayjs(startDate).format('YYYY')}`;
    } else if (unit === 'month') {
      return `${dayjs(startDate).format('MMM YYYY')}`;
    } else if (unit === 'hour') {
      return `${dayjs(startDate).format('DD MMM HH:mm')}`;
    }
    return dayjs(startDate).format('DD MMM');
  }

  private formatDateRangeByUnit(startDate: string, endDate: string, unit: string): string {
    const start = dayjs(startDate);
    const end = dayjs(endDate);

    switch (unit) {
      case 'hour':
        // For hourly: show date with hour range
        if (start.isSame(end, 'day')) {
          return `${start.format('DD MMM')} (${start.format('HH:mm')} - ${end.format('HH:mm')})`;
        }
        return `${start.format('DD MMM HH:mm')} - ${end.format('DD MMM HH:mm')}`;
      
      case 'day':
        // For daily: show date range
        if (start.isSame(end, 'month')) {
          return `${start.format('DD')} - ${end.format('DD MMM YYYY')}`;
        }
        return `${start.format('DD MMM')} - ${end.format('DD MMM YYYY')}`;
      
      case 'week':
        // For weekly: show date range
        return `${start.format('DD MMM')} - ${end.format('DD MMM YYYY')}`;
      
      case 'month':
        // For monthly: show month range
        if (start.isSame(end, 'year')) {
          return `${start.format('MMM')} - ${end.format('MMM YYYY')}`;
        }
        return `${start.format('MMM YYYY')} - ${end.format('MMM YYYY')}`;
      
      case 'quarter':
        // For quarterly: show quarter range
        return `Q${start.quarter()} ${start.format('YYYY')} - Q${end.quarter()} ${end.format('YYYY')}`;
      
      case 'year':
        // For yearly: show year range
        if (start.year() === end.year()) {
          return start.format('YYYY');
        }
        return `${start.format('YYYY')} - ${end.format('YYYY')}`;
      
      default:
        return `${start.format('MMM YYYY')} - ${end.format('MMM YYYY')}`;
    }
  }

  private fetchData(
    useCache: boolean = false,
    useLoader: boolean = true,
    isPrevious: boolean = false
  ) {
    if (!this.dateRange) {
      console.warn('DateRange not available yet');
      return;
    }

    this.isLoading.set(true)
    
    // Clear existing data before fetching
    if (isPrevious) {
      this.previousChartData = [];
      this.previousTableData = [];
      this.previousTotal = 0;
    } else {
      this.chartData = [];
      this.tableData = [];
      this.currentTotal = 0;
    }

    const payload: any = {
      start_time: dayjs(isPrevious
        ? this.dateRange.previous_start_date
        : this.dateRange.start_date || new Date()).format('YYYY-MM-DD'),
      end_time: dayjs(isPrevious
        ? this.dateRange.previous_end_date
        : this.dateRange.end_date || new Date()).format('YYYY-MM-DD'),
      is_dummy: 0,
      cocu_id: this.cocu_id,
      country_id: this.country_id,
      // timezone: 'PST',
      unit: this.dateRange?.unit || 'day',
      outputFormat: 'db',
      // Include stage or phase filter based on selection
      ...{ type: this.selectedStageId},
    };

    if (this.productServerName === 'credee') {
      payload['timezone'] = 'PDT';
    }

    this.apiService
      .post(`/visionboard/stats/contract_stats_new`, payload, {
        addAccessToken: this.requiresAccessToken,
        productServerName: this.productServerName,
        showErrorToaster: true,
        showSuccessToaster: false,
        showLoader: false,
      })
      .subscribe({
        next: (response: any) => {
          if (response?.is_error === 0 && response?.data) {
            this.isLoading.set(false)
            const unit = this.dateRange?.unit || 'day';
            
            // Transform records to chart format
            const chartData = (response.data.records || []).map((item: any) => ({
              start: item.start,
              end: item.end,
              value: item.count,
            }));

            // Transform records to table format with formatted dates
            const tableData = (response.data.records || []).map((item: any) => ({
              start: this.formatStartDate(item.start, item.end, unit),
              total: item.count || 0,
              // Include contract type breakdown from API
              noFee: item.nofee || 0,
              tenPercent: item.tenPercent || 0,
              inhouse: item.inhouse || 0,
              ez: item.ez || 0,
              ez_deferred: item.ez_deferred || 0,
              ar: item.ar || 0,
            }));

            response?.types.map((type: any) => {
              type.label = type.name;
              type.value = type.type;
            });

            this.selectorOptions = response.types || [];

            // Update data based on whether this is previous period data
            if (isPrevious) {
              // Update previous period data
              this.previousChartData = (chartData || []).map((record: any) => ({
                ...record,
                key: 'Previous Contracts'
              }));
              this.previousTableData = tableData;
              this.previousTotal = response.data.stats?.count || 0;
              
              // Recalculate percent change
              if (this.previousTotal > 0) {
                this.percentChange = ((this.currentTotal - this.previousTotal) / this.previousTotal) * 100;
              }
            } else {
              // Update current period data
              this.chartData = (chartData || []).map((record: any) => ({
                ...record,
                key: 'Current Contracts'
              }));
              this.tableData = tableData;
              this.currentTotal = response.data.stats?.count || 0;
              
              // Clear previous data if comparison period is not selected
              if (!(this.dateRange.previous_start_date && this.dateRange.previous_end_date)) {
                this.previousChartData = [];
                this.previousTableData = [];
                this.previousTotal = 0;
                this.percentChange = 0;
              }
            }
          }
        },
        error: (error: any) => {
          this.isLoading.set(false)
          console.error(`Error fetching contract stats:`, error);
        }
      });
  }

  onToggle(event: any): void {
    if (event === 'table' || event === 'graph') {
      this.viewSignal.set(event === 'table' ? 'table' : 'chart');
      return;
    }

    let value: boolean | undefined;
    if (typeof event === 'boolean') value = event;
    else if (event && typeof event === 'object') {
      value = (event.value ?? event.detail ?? event.payload ?? event.checked) as any;
      if (typeof value !== 'boolean') {
        if ('isTable' in event) value = !!event.isTable;
        else value = event === 'table' ? true : undefined;
      }
    }

    this.viewSignal.set(value ? 'table' : 'chart');
  }

  onStagePhaseChange(value: number | string): void {
    this.selectedStageId = value as number;
    // Reload data when stage/phase selection changes
    if (this.dateRange) {
      if (this.dateRange?.previous_start_date && this.dateRange?.previous_end_date) {
        this.fetchData(false, true, false);
        this.fetchData(false, true, true);
      } else {
        this.fetchData();
      }
    }
  }

  onSelectionChange(value: number | string | (number | string)[]): void {
    // Handle single selection (array should never come through in single mode, but type-safe)
    const singleValue = Array.isArray(value) ? value[0] : value;
    if (singleValue !== undefined) {
      this.onStagePhaseChange(singleValue);
    }
  }

  onCellHover(event: any) {
    if (event && event.row && event.event) {
      this.hoveredRowData = event.row;
      this.tooltipVisible = true;
      
      const mouseEvent = event.event as MouseEvent;
      const target = mouseEvent.currentTarget as HTMLElement;
      const tableContainer = target.closest('.overflow-hidden');
      
      if (tableContainer) {
        const rect = tableContainer.getBoundingClientRect();
        const relativeX = mouseEvent.clientX - rect.left;
        const relativeY = mouseEvent.clientY - rect.top;
        
        const estimatedTooltipHeight = 130;
        const estimatedTooltipWidth = 170;
        
        let x = relativeX + 25;
        let y = relativeY - 30;
        
        if (y + estimatedTooltipHeight > rect.height) {
          y = relativeY - estimatedTooltipHeight - 10;
        }
        
        if (y < 10) {
          y = 10;
        }
        
        if (x + estimatedTooltipWidth > rect.width) {
          x = relativeX - estimatedTooltipWidth - 25;
        }
        
        if (x < 10) {
          x = 10;
        }
        
        this.tooltipPosition = { x, y };
      } else {
        this.tooltipPosition = {
          x: mouseEvent.offsetX + 25,
          y: mouseEvent.offsetY - 30
        };
      }
    } else {
      this.tooltipVisible = false;
      this.hoveredRowData = null;
    }
  }

}
